import { useState, useEffect } from "react";
import { Box, IconButton, CircularProgress, Typography } from "@mui/material";
import { MoreVert as MoreVertIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ActionsMenu from "../../components/ActionsMenu";
import DeleteConfirmationDialog from "../../components/DeleteDialog";
import DataTable from "../../components/DataTable";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from "react-i18next";

const Expenses = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const apiUrl = "https://oven-backend.onrender.com";

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        const response = await axios.get(`${apiUrl}/expenses`);
        setExpenses(response.data);
      } catch (error) {
        console.error("Error fetching expenses:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchExpenses();
  }, []);

  const handleMenuOpen = (event, expenseId) => {
    setAnchorEl(event.currentTarget);
    setSelectedExpenseId(expenseId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditExpense = () => {
    navigate(`/UpdateExpenses/${selectedExpenseId}`);
    handleMenuClose();
  };

  const handleViewDetails = () => {
    navigate(`/ViewExpense/${selectedExpenseId}`);
    handleMenuClose();
  };

  const handleDeleteDialogOpen = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await axios.delete(`${apiUrl}/expenses/${selectedExpenseId}`);
      setExpenses((prevExpenses) => prevExpenses.filter(expense => expense._id !== selectedExpenseId));
      console.log("Expense Data:", res.data);
    } catch (error) {
      console.error("Error deleting expense:", error);
    } finally {
      handleDeleteDialogClose();
    }
  };

  const columns = [
    { 
      field: "employee_id", 
      headerName: t("employee"), 
      flex: 1, 
      valueGetter: (params) => params.row.employee_id?.name || "N/A" 
    },
    { 
      field: "category", 
      headerName: t("category"), 
      flex: 1 
    },
    { 
      field: "amount", 
      headerName: t("amount"), 
      flex: 1, 
      renderCell: (params) => (
        <Typography color="green">
          ${params.row.amount}
        </Typography>
      ),
    },
    { 
      field: "date", 
      headerName: t("date"), 
      flex: 1, 
      valueGetter: ({ value }) => value ? new Date(value).toLocaleDateString() : "N/A" 
    },
    { field: "payment_status", headerName: t("payment_status"), flex: 1 },
    { field: "inventory_status", headerName: t("expenses_status"), flex: 1 },
    {
      field: "actions",
      headerName: t("actions"),
      flex: 0.5,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={(event) => handleMenuOpen(event, params.row._id)}>
            <MoreVertIcon />
          </IconButton>
          <ActionsMenu 
            anchorEl={anchorEl}
            handleMenuClose={handleMenuClose}
            menuItems={[
              { label: t("edit_expense"), action: handleEditExpense, icon: <EditIcon /> },
              { label: t("view_details"), action: handleViewDetails, icon: <VisibilityIcon />},
              { label: t("delete_expense"), action: handleDeleteDialogOpen, icon: <DeleteIcon />, style: { color: "red" } }
            ]}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px" sx={{ direction: isRTL ? "rtl" : "ltr" }}>
      {loading ? (
        <CircularProgress />
      ) : (
        <DataTable 
          title={t("expenses_list")}
          columns={columns}
          rows={expenses} 
          getRowId={(row) => row._id}
        />
      )}

      <DeleteConfirmationDialog
        open={openDeleteDialog} 
        onClose={handleDeleteDialogClose} 
        onConfirm={handleConfirmDelete} 
        title={t("delete_expense")}
        message={t("delete_expense_confirm")}
        confirmColor="error"
        cancelColor="secondary"
        size="large"
      />
    </Box>
  );
};

export default Expenses;
